import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Menu as AntMenu, Tag } from 'antd';
import { PlusOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Bidding.scss';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bidingService } from "../../services/BidingService";
import { useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import {
    buildCategoryOptionsFromEndpoint,
    createBackendCategoryPathResolver,
    createBackendCategoryResolver,
    filterProductsBySearchAndCategories,
    mergeCategoryOptionsWithProducts,
    resolveStatusTagProps
} from './biddingFilters';
import { useDesktopTableScroll } from './useDesktopTableScroll';
import {
    buildLastWinningPriceMap,
    createLastWinningPriceColumn,
    mergeLastWinningPricesIntoProducts
} from './renderLastWinningPriceCell';
import { createProductNameCellRenderer } from './renderProductNameCell';
import { useActiveBiddingGuard } from './useActiveBiddingGuard';

import SupplierMenu from '../../components/SupplierMenu/SupplierMenu'
const { Search } = Input;

const HiddenProducts = () => {
    const navigate = useNavigate();

    const { id } = useParams();
    const [endTime, setEndTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState('--:--');
    const location = useLocation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState(['all']);
    const [listId, setListId] = useState(null);
    const [biddingCounts, setBiddingCounts] = useState(null);
    const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
    const [lastWinningPriceMap, setLastWinningPriceMap] = useState({});
    const { canLoadBiddingData } = useActiveBiddingGuard(id, navigate);


    const pathname = location.pathname;

    let selectedMenuKey = null;
    if (pathname.startsWith('/produse-ofertare')) {
        selectedMenuKey = 'produse';
    } else if (pathname.startsWith('/produse-ascunse')) {
        selectedMenuKey = 'ascunse';
    } else if (pathname.startsWith('/oferte-inregistrate')) {
        selectedMenuKey = 'oferte';
    }

    const refreshBiddingCounts = useCallback(() => {
        if (!id) return Promise.resolve();
        return bidingService.getBiddingCounts(id)
            .then((res) => {
                setBiddingCounts(res?.data || null);
            })
            .catch((err) => {
                console.warn('Failed to refresh bidding_counts', err);
            });
    }, [id]);

    useEffect(() => {
        if (id && canLoadBiddingData) {
            setLoading(true);
            refreshBiddingCounts();
            bidingService.getBiddingCategories(id)
                .then((categoryRes) => {
                    setApiCategoryOptions(
                        buildCategoryOptionsFromEndpoint(categoryRes?.data, ['hidden_products'])
                    );
                    return bidingService.getHiddenProducts(id).then((hiddenRes) => ({
                        categoryData: categoryRes?.data,
                        hiddenData: hiddenRes?.data
                    }));
                })
                .then(({ categoryData, hiddenData }) => {
                    const items = hiddenData?.hidden_items || [];
                    const resolveCategoryLabel = createBackendCategoryResolver(categoryData);
                    const resolveCategoryPath = createBackendCategoryPathResolver(categoryData);
                    setListId(hiddenData.necessity_list.id);
                    const formattedProducts = items.map(item => ({
                        key: item.id,
                        name: item.product?.name,
                        barcode: item.product?.barcode,
                        category: resolveCategoryLabel(item),
                        category_path: resolveCategoryPath(item),
                        quantity: item.quantity,
                        unit_measurement: item.unit_measurement,
                        instructions: item.supplementary_info,
                        statusTag: resolveStatusTagProps(item.status),
                        hasBid: !!item.current_user_bid,
                        offer: item.current_user_bid?.price ?? null,
                        originalData: item
                    }));
                    setProducts(formattedProducts);
                })
                .catch((err) => {
                    console.error("Error fetching hidden products/categories:", err);
                    toast.error("A apărut o eroare la încărcarea produselor ascunse sau categoriilor.");
                })
                .finally(() => setLoading(false));
            bidingService.getList(id)
                .then((res) => {
                    const biddingEndTime = res?.data?.bidding_end_time;
                    if (biddingEndTime) {
                        setEndTime(new Date(biddingEndTime));
                    }
                })
                .catch((err) => {
                    console.warn('Failed to fetch bidding end time', err);
                });
        }
    }, [id, canLoadBiddingData, refreshBiddingCounts]);
    useEffect(() => {
        if (!id || !canLoadBiddingData) return;

        bidingService.getLastWinningPrices(id)
            .then((res) => {
                setLastWinningPriceMap(buildLastWinningPriceMap(res?.data));
            })
            .catch((err) => {
                console.warn('Failed to fetch last winning prices', err);
            });
    }, [id, canLoadBiddingData]);
    useEffect(() => {
        setProducts((previousProducts) => {
            const nextProducts = mergeLastWinningPricesIntoProducts(previousProducts, lastWinningPriceMap);
            const hasChanges = nextProducts.some((product, index) => (
                product.lastWinningPrice !== previousProducts[index]?.lastWinningPrice
                || product.lastWinningWonAt !== previousProducts[index]?.lastWinningWonAt
            ));

            return hasChanges ? nextProducts : previousProducts;
        });
    }, [products, lastWinningPriceMap]);

    useEffect(() => {
        if (!endTime) {
            setTimeLeft('--:--');
            return;
        }

        const interval = setInterval(() => {
            const now = new Date();
            const difference = endTime - now;

            if (difference <= 0) {
                clearInterval(interval);
                setTimeLeft('00:00');
            } else {
                const minutes = Math.floor(difference / 1000 / 60);
                const seconds = Math.floor((difference / 1000) % 60);
                setTimeLeft(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} minute`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [endTime]);

    const pickBiddingCount = (...keys) => {
        const roots = [biddingCounts, biddingCounts?.counts, biddingCounts?.data];
        for (const root of roots) {
            if (!root) continue;
            for (const k of keys) {
                const v = root?.[k];
                if (v === null || v === undefined) continue;
                const parsedValue = Number(v);
                if (Number.isFinite(parsedValue)) return parsedValue;
            }
        }
        return undefined;
    };

    const categoryOptions = useMemo(
        () => mergeCategoryOptionsWithProducts(apiCategoryOptions, products),
        [apiCategoryOptions, products]
    );
    const filteredProducts = useMemo(
        () => filterProductsBySearchAndCategories(products, searchQuery, selectedCategories),
        [products, searchQuery, selectedCategories]
    );
    const { tableContentRef, footerRef, tableScrollConfig } = useDesktopTableScroll([filteredProducts.length]);
    const renderProductNameCell = useMemo(() => createProductNameCellRenderer(), []);

    const handleSearch = (value) => {
        setSearchQuery(value || '');
    };

    const unhideProduct = record => {
        const productId = record.originalData.id;
        const status = 'show';

        bidingService.unhideProduct(listId, productId, status)
            .then(() => {
                toast.success(`Produsul ${record.name} a fost adaugat cu succes.`);
                setProducts(prev => prev.filter(p => p.key !== record.key));
                refreshBiddingCounts();
            })
            .catch((err) => {
                console.error("Eroare la adaugarea produsului:", err);
                toast.error("A apărut o eroare la adaugarea produsului.");
            });
    }

    const columns = [
        {
            title: 'Nume produs',
            dataIndex: 'name',
            key: 'name',
            width: 280,
            render: renderProductNameCell
        },
        {
            title: 'Cod de bare',
            dataIndex: 'barcode',
            key: 'barcode',
            width: 230,
            render: (barcode) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ color: '#000' }}>{barcode}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            type="link"
                            onClick={() => {
                                navigator.clipboard.writeText(barcode);
                                toast.success('Codul de bare a fost copiat!');
                            }}
                            style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                        >
                            <span
                                style={{
                                    backgroundColor: 'gray',
                                    borderRadius: '50%',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                }}
                            >
                                <CopyOutlined style={{ color: '#fff', fontSize: '14px' }} />
                            </span>
                        </Button>
                    </div>
                </div>
            ),
        },
        {
            title: 'Cantitate',
            key: 'quantity',
            width: 130,
            render: (_, record) => `${record.quantity} ${record.unit_measurement || ''}`,
        },
        createLastWinningPriceColumn(),
        {
            title: 'Status',
            key: 'status',
            width: 140,
            render: (_, record) => (
                <Tag color={record.statusTag?.color || 'default'}>
                    {record.statusTag?.label || 'Necunoscut'}
                </Tag>
            ),
        },

        {
            title: 'Acțiuni',
            key: 'add',
            width: 110,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<PlusOutlined style={{ color: 'red' }} />}
                    onClick={() => unhideProduct(record)}
                />
            ),
        },
    ];


    return (
        <div className='standard products list biding'>
            <Header />
            <div className='standard__mobile-header'>
                <img src='/images/logo.png' alt='logo' className='standard__only-mobile standard__mobile-logo' />
            </div>

            <div className='standard__container list '>
                <SupplierMenu
                    categoryOptions={categoryOptions}
                    selectedCategories={selectedCategories}
                    onCategoriesChange={setSelectedCategories}
                />

                <div className='standard__wrapper biding__wrapper'>
                    <div className='biding__content'>
                        <div className='biding__header'>
                            <div className='biding__header-main'>
                                <h2 className='standard__page-title'>Produse ascunse</h2>
                                <h4 className='standard__page-info'>
                                    Aici găsiți produsele eliminate din listele de ofertare. Pentru a le oferta din nou, adăugați-le înapoi în liste,<b> apăsând butonul ”+” din dreapta fiecărui produs</b>.
                                </h4>
                            </div>
                            <div className='biding__header-side'>
                                <div className='biding__top-area'>
                                    <div className='list__search-box biding__search-box'>
                                        <Search
                                            className="list__search-input"
                                            placeholder="Caută produs..."
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchQuery}
                                            enterButton
                                            allowClear
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <AntMenu
                            mode="horizontal"
                            selectedKeys={[selectedMenuKey]}
                            onClick={(e) => {
                                switch (e.key) {
                                    case 'produse':
                                        navigate(`/produse-ofertare/${id}`);
                                        break;
                                    case 'ascunse':
                                        navigate(`/produse-ascunse/${id}`);
                                        break;
                                    case 'oferte':
                                        navigate(`/oferte-inregistrate/${id}`);
                                        break;

                                    default:
                                        break;
                                }
                            }}
                        >
                            <AntMenu.Item key="produse">
                                Produse pentru ofertare{typeof pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count') === 'number' ? ` (${pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="ascunse">
                                Produse ascunse{typeof pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count') === 'number' ? ` (${pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="oferte">
                                Oferte inregistrate{typeof pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count') === 'number' ? ` (${pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count')})` : ''}
                            </AntMenu.Item>
                        </AntMenu>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>



                        </div>

                        <div className="list__table-wrapper">

                            <div className='list__table-content' ref={tableContentRef}>
                                <Table
                                    dataSource={filteredProducts}
                                    columns={columns}
                                    pagination={false}
                                    rowKey="key"
                                    loading={loading}
                                    scroll={{ ...(tableScrollConfig || {}), x: 1600 }}
                                    tableLayout="fixed"
                                />

                            </div>
                            <div className='biding__bottom-wrapper' ref={footerRef}>
                                <div className='biding__time-box'>
                                    <div className='biding__time'>
                                        <h5>Timp ramas pentru ofertare: </h5>
                                        <h4>{timeLeft}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>
    );
};

export default HiddenProducts;